import { useEffect, useState, useMemo, useCallback } from 'react'
import styles from './ScoopHistory.module.scss'
import {
    ScoopTable,
    ScoopButton,
    ScoopText,
    ScoopStatusBubble,
    ScoopColumn,
    useToast,
} from '../../../../ui'
import { useModal, MODALS } from '../../../../modals'
import HistoryDetailsDrawer from '../HistoryDetailsDrawer/HistoryDetailsDrawer'
import { useEventLogItems } from '../../../../../api/hooks/datasource/hook'
import { DatabaseIcon, TrashIcon, XCircleIcon } from '@phosphor-icons/react'
const matchS3key = (str1, str2) => {
    if (str1 && str2) {
        // Normalize the strings by replacing spaces with '+' and vice versa
        let normalizedStr1 = str1.replace(/ /g, '+')
        let normalizedStr2 = str2.replace(/ /g, '+')

        return normalizedStr1 === normalizedStr2
    }
    return false
}

export const ScoopHistory = ({
    ingestionLogs,
    refreshLogs,
    inboxID,
    tables,
    onReprocess,
    processStatus,
    dataset,
}) => {
    const [pending] = useState(-1)
    const [openDrawer, setOpenDrawer] = useState(null)
    const toast = useToast()
    const [errorMessage, setErrorMessage] = useState(null)
    const [isDeleteLoading, setIsDeleteLoading] = useState(false)

    const deleteModal = useModal(MODALS.COMMON.DELETE)

    const { eventLogItems: eventLogs, isLoading, deleteEventLogItem } = useEventLogItems(inboxID)

    useEffect(() => {
        if (Array.isArray(eventLogs) && eventLogs.length > 0 && eventLogs[0]?.error) {
            setErrorMessage(eventLogs[0].message)
        } else {
            setErrorMessage(null)
        }
    }, [eventLogs])

    // Enrich logs with details from eventLogs
    const enrichedLogs = useMemo(() => {
        if (!ingestionLogs) return []
        return ingestionLogs.map((log) => {
            const logDetail = eventLogs?.find((detail) => matchS3key(detail.s3Key, log.s3key))
            const tableName = tables?.find(
                (table) => table.reportSeriesTableID === log.reportSeriesTableID
            )?.tableName
            return {
                ...log,
                detail: logDetail,
                tableName,
            }
        })
    }, [ingestionLogs, eventLogs, tables])

    const handleDeleteLog = async (response) => {
        await refreshLogs()
        toast.success(response.result ?? 'Log deleted successfully')
        setIsDeleteLoading(false)
    }

    const handleConfirmDelete = async (log) => {
        await deleteModal.show({
            title: 'Scoop Log',
            description:
                "Are you sure you want to delete this Scoop log? You won't be able to access the data in this log again.",
            onDelete: () => {
                setIsDeleteLoading(true)
                deleteEventLogItem({ deleteLog: log, onSuccess: handleDeleteLog })
            },
        })
    }

    const handleOpenDrawer = useCallback(
        (log) => {
            setOpenDrawer({
                ...eventLogs?.find((logDetail) => matchS3key(logDetail.s3Key, log.s3key)),
                ...log,
                tableName: log.tableName,
                inboxID: inboxID,
            })
        },
        [eventLogs, inboxID]
    )

    const columns = useMemo(
        () => [
            {
                field: 'inferredDate',
                header: 'Date',
                flex: 1,
            },
            {
                field: 'rstiKey',
                header: 'Status',
                width: 120,
                render: ({ row }) => (
                    <ScoopStatusBubble status={row.rstiKey ? 'Success' : 'Failed'} withLabel />
                ),
            },
            {
                field: 'tableName',
                header: 'Table',
                flex: 1,
            },
            {
                field: 'details',
                header: 'Details',
                flex: 2,
                render: ({ row }) => (
                    <div className={styles.detailsContent}>
                        <span>{row.detail?.message}</span>
                        <div>
                            {pending === row.id ? (
                                <ScoopButton variant="icon" size="xs" className={styles.iconButton}>
                                    <XCircleIcon
                                        size={20}
                                        weight="regular"
                                        className={styles.iconImage}
                                    />
                                </ScoopButton>
                            ) : (
                                <div className={styles.actionsContainer}>
                                    <ScoopButton
                                        variant="text"
                                        size="xs"
                                        className={styles.iconButton}
                                        onClick={() => handleOpenDrawer(row)}
                                    >
                                        <DatabaseIcon
                                            size={20}
                                            weight="regular"
                                            className={styles.iconImage}
                                        />
                                    </ScoopButton>
                                    <ScoopButton
                                        variant="text"
                                        size="xs"
                                        className={styles.iconButton}
                                        onClick={() => handleConfirmDelete(row)}
                                    >
                                        <TrashIcon
                                            size={20}
                                            weight="regular"
                                            className={styles.iconImage}
                                        />
                                    </ScoopButton>
                                </div>
                            )}
                        </div>
                    </div>
                ),
            },
        ],
        [pending, handleOpenDrawer]
    )

    return (
        <>
            <div className={styles.container}>
                {errorMessage && (
                    <ScoopColumn className={styles.errorBox}>
                        <ScoopText className={styles.errorText}>Error: {errorMessage}</ScoopText>
                    </ScoopColumn>
                )}
                <ScoopTable
                    columns={columns}
                    rows={enrichedLogs}
                    loading={isLoading}
                    emptyMessage="No dataset load history"
                    pagination={{
                        enabled: true,
                        pageSize: 10,
                        pageSizeOptions: [10, 25, 50],
                    }}
                    domLayout="autoHeight"
                    className={styles.table}
                />
            </div>
            <HistoryDetailsDrawer
                open={Boolean(openDrawer)}
                onClose={() => setOpenDrawer(null)}
                logDetails={openDrawer}
                dataset={dataset}
                onReprocess={onReprocess}
            />
        </>
    )
}
export default ScoopHistory
